import {HttpErrorResponse, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {inject} from '@angular/core';
import {catchError, switchMap, throwError} from 'rxjs';
import {AuthService} from './auth.service';
import {ToastrService} from 'ngx-toastr';
import {ClientLogService} from './client-log.service';
import {Router} from '@angular/router';
import {SessionExpiredService} from './session-expired.service';
import {SystemStateService} from './system-state.service';
import {isHttpRequestCancellation} from './http-error.util';
import {SKIP_GLOBAL_LOADER} from '../core/http-context';
import {isAuthSessionInvalidPayload, isDefinitiveAuthRefreshFailure} from './auth-session.util';

function handleConnectivityError(
    error: HttpErrorResponse,
    systemStateService: SystemStateService,
    requestNavigationGeneration: number,
    showToast: boolean,
    toastr?: ToastrService,
): void {
    // Cancelled in-flight requests must not block the next route (C30-N01).
    if (isHttpRequestCancellation(error)) {
        return;
    }
    // Stale failures after NavigationStart belong to a previous page lifecycle.
    if (!systemStateService.isCurrentNavigation(requestNavigationGeneration)) {
        return;
    }

    if (error.status === 0) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            systemStateService.showOffline();
        } else {
            systemStateService.showServerUnavailable();
        }
        if (showToast && toastr) {
            toastr.error('Please check your internet connection or try again later.', 'Error');
        }
        return;
    }

    if (error.status === 502 || error.status === 503 || error.status === 504 || error.status >= 500) {
        systemStateService.showServerUnavailable();
    }
}

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toastr = inject(ToastrService);
  const clientLogger = inject(ClientLogService);
  const router = inject(Router);
  const sessionExpiredService = inject(SessionExpiredService);
    const systemStateService = inject(SystemStateService);
    const sessionExpiredMessage = 'Your session has expired. Please Login Again.';
  const subscriptionsDisabledMessage = 'Subscriptions are disabled for your organization. Please contact your administrator.';
  const requestNavigationGeneration = systemStateService.getNavigationGeneration();

  // Skip authentication for login and password reset endpoints
  const url = req.url.toLowerCase();
  const isLoginEndpoint = /\/login\/?($|\?)/.test(url);
  const isPasswordResetEndpoint = /\/password-reset(\/|$|\?)/.test(url);
  const isTokenRefreshEndpoint = /\/token\/refresh\/?($|\?)/.test(url);
  const isClientLogsEndpoint = /\/client-logs\/?($|\?)/.test(url);

    // /platform-health/ requires JWT (Kyra Super User) — do not skip auth.
    if (isLoginEndpoint || isPasswordResetEndpoint || isTokenRefreshEndpoint || isClientLogsEndpoint) {
    return next(withSkipGlobalLoader(req)).pipe(
      catchError((error: HttpErrorResponse) => {
          handleConnectivityError(
              error,
              systemStateService,
              requestNavigationGeneration,
              true,
              toastr,
          );
          if (!isHttpRequestCancellation(error) && (error.status >= 500 || error.status === 0)) {
              clientLogger.log(
                  'ERROR',
                  `HTTP ${error.status} for ${req.url}`,
                  error.message,
                  {status: error.status}
              );
          }
        return throwError(() => error);
        })
      );
  }

  // Add auth header with access token
  const accessToken = authService.getToken();
  const clonedRequest = addAuthenticationHeader(req, accessToken);

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Access token expired, try to refresh
        return authService.refreshToken().pipe(
          switchMap(newToken => {
            // Retry without incrementing global loader (outer request already counted)
            const updatedRequest = withSkipGlobalLoader(addAuthenticationHeader(req, newToken));
            return next(updatedRequest);
          }),
          catchError(refreshError => {
              // Only definitive auth failures hard-logout (aligned with AuthGuard).
              // Network / 5xx preserve credentials for retry.
              if (isDefinitiveAuthRefreshFailure(refreshError)) {
                  sessionExpiredService.forceExpire('interceptor-refresh-invalid');
            }
            return throwError(() => refreshError);
          })
        );
      }

      if (error.status === 403) {
          if (isAuthSessionInvalidPayload(error)) {
              sessionExpiredService.forceExpire('interceptor-token-forbidden');
          return throwError(() => error);
        }

        const detail = error?.error?.detail;
        const isSubscriptionsDisabled = typeof detail === 'string' &&
          detail.toLowerCase().includes('subscriptions are disabled for this organization');

        if (isSubscriptionsDisabled) {
          toastr.error(subscriptionsDisabledMessage, 'Access Disabled');
          if (router.url.startsWith('/subscriptions')) {
            router.navigate(['/dashboard']);
          }
        } else if (error.error && error.error.messages) {
          error.error.messages.forEach((element:any) => {
            toastr.error(element.message, 'Error');
          });
        } else if (detail) {
          toastr.error(detail, 'Error');
        }
      }

        if (error.status === 413) {
            toastr.error(
                'File is too large to upload. Please choose a smaller file.',
                'File Too Large',
            );
            return throwError(() => error);
        }

      if (error.status === 0 || error.status >= 500) {
          handleConnectivityError(
              error,
              systemStateService,
              requestNavigationGeneration,
              false,
          );
      }

      if (!isHttpRequestCancellation(error) && (error.status >= 500 || error.status === 0)) {
        clientLogger.log(
          'ERROR',
          `HTTP ${error.status} for ${req.url}`,
          error.message,
          { status: error.status }
        );
      }
      
      return throwError(() => error);
    })
  );
};

function addAuthenticationHeader(req: HttpRequest<unknown>, token: string | null) {
  if (!token) return req;
  
  return req.clone({
    setHeaders: {
      // "Access-Control-Allow-Origin": ' http://localhost:4200/', 
      Authorization: `Bearer ${token}`
      // 'Cache-Control': 'no-cache',
      //   'Pragma': 'no-cache',
      //   'Expires': '0'
    }
  });
}

function withSkipGlobalLoader(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.clone({
    context: req.context.set(SKIP_GLOBAL_LOADER, true),
  });
}

